"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import AskHoopsButton from "@/components/ui/AskHoopsButton";
import type { SmartAccountBalances } from "@/hooks/useSmartAccountBalances";

interface AISectionProps {
  balances: SmartAccountBalances;
  smartAccountAddress?: string;
}

export function AISection({ balances, smartAccountAddress }: AISectionProps) {
  const router = useRouter();

  const handleAsk = useCallback(() => {
    const ctx = {
      address: smartAccountAddress ?? "unknown",
      timestamp: new Date().toISOString(),
      balances: {
        USDC: { amount: balances.usdc, usdValue: balances.usdc },
        XLM: {
          amount: balances.xlm,
          usdValue: +(balances.xlm * balances.xlmPrice).toFixed(2),
        },
      },
      strategies: {
        "Aqua LP (USDC/XLM)": {
          shares: balances.aquaLp,
          usdValue: +balances.aquaLpValue.toFixed(2),
        },
        "Soroswap LP (USDC/XLM)": {
          shares: balances.soroswapLp,
          usdValue: +balances.soroswapLpValue.toFixed(2),
        },
      },
      totalPortfolioUsd: +(
        balances.usdc +
        balances.xlm * balances.xlmPrice +
        balances.aquaLpValue +
        balances.soroswapLpValue
      ).toFixed(2),
    };

    try {
      sessionStorage.setItem("hoops_portfolio_context", JSON.stringify(ctx));
    } catch {
      // Quota exceeded — continue without context
    }

    const prompt =
      "Evaluate my current portfolio — analyze my risk exposure, yield performance, and suggest optimizations.";
    router.push(
      "/assistant/chat?q=" + encodeURIComponent(prompt) + "&portfolioCtx=1",
    );
  }, [router, balances, smartAccountAddress]);

  return (
    <div
      className="px-5 py-5 rounded-xl flex flex-col gap-4"
      style={{ background: "var(--surface)", border: "1px solid var(--border-default)" }}
      aria-label="Optimize portfolio with AI"
    >
      {/* Text block */}
      <div>
        <p
          className="font-serif font-normal"
          style={{ fontSize: "1.1rem", color: "var(--text-primary)" }}
        >
          Optimize your portfolio with AI
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
          Get real-time risk analysis, yield optimization, and strategy recommendations
        </p>
      </div>

      {/* Full-width Ask Hoops button */}
      <AskHoopsButton
        className="w-full [&>button]:w-full [&>button]:rounded-xl [&>button]:justify-center"
        onClick={handleAsk}
      />
    </div>
  );
}
